import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CrystalIcon } from './CrystalIcon';
import { X, Plus } from 'lucide-react';
import { HIT_KINDS, HIT_KIND_META, DAILY_HIT_GOAL, PILLARS } from './oathTheme';

const P = PILLARS[1];
export const kindOf = (id) => HIT_KIND_META[id] || HIT_KIND_META.win;

/* Glass vault that fills with gold coins — one per gratitude today. */
const GratitudeVault = ({ count }) => {
  const fill = Math.min(1, count / DAILY_HIT_GOAL);
  return (
    <div className="flex items-center gap-4 rounded-2xl px-4 py-3" data-testid="oath-vault"
      style={{ background: 'linear-gradient(135deg, rgba(91,224,138,0.10), rgba(212,175,55,0.10))', border: `1px solid ${P.accent}35` }}>
      <div className="relative w-14 h-16 rounded-b-2xl rounded-t-md overflow-hidden" style={{ border: '1.5px solid rgba(255,233,160,0.5)', background: 'rgba(255,255,255,0.03)' }}>
        <motion.div className="absolute bottom-0 left-0 right-0" animate={{ height: `${fill * 100}%` }} transition={{ type: 'spring', stiffness: 120 }}
          style={{ background: 'linear-gradient(0deg, #B8860B, #FFD45C)', boxShadow: '0 0 16px rgba(255,212,92,0.6)' }} />
        <span className="absolute inset-0 flex items-center justify-center font-['Outfit'] font-semibold text-lg" style={{ color: fill > 0.5 ? '#2a1a00' : '#FFE9A0' }} data-testid="oath-hits-today">{count}</span>
      </div>
      <div className="flex-1">
        <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-white/45">Gratitude Vault · today</p>
        <p className="font-['Outfit'] text-sm text-white/80 mt-1">
          {count >= DAILY_HIT_GOAL ? 'RUSH active — every extra gratitude today earns DOUBLE power (+10)' : `${DAILY_HIT_GOAL - count} more to unlock RUSH: double power (+10) on every extra gratitude today`}
        </p>
      </div>
    </div>
  );
};

export const OathHits = ({ state, onLog, onDelete, busy }) => {
  const [kind, setKind] = useState('win');
  const [text, setText] = useState('');
  const today = state.hits_week.filter((h) => h.date === state.today);
  const k = kindOf(kind);

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    onLog(kind, text.trim());
    setText('');
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="font-['Cinzel'] text-sm tracking-[0.15em]" style={{ color: P.light }}>Why gratitude?</p>
        <p className="font-['Outfit'] text-xs text-white/50 mt-1">Your brain is wired to notice problems. Every time you log something good, you train it to hunt for wins instead — and you feel richer, calmer and stronger. One line is enough.</p>
      </div>
      <GratitudeVault count={state.hits_today} />
      <div>
        <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">1 · What are you grateful for?</p>
        <div className="grid grid-cols-3 gap-2">
          {HIT_KINDS.map((id) => {
            const m = HIT_KIND_META[id];
            const on = kind === id;
            return (
              <button key={id} type="button" onClick={() => setKind(id)} data-testid={`oath-kind-${id}`}
                className="rounded-2xl py-3 px-1 flex flex-col items-center gap-1 transition-transform duration-200 active:scale-95"
                style={{ border: `1px solid ${on ? m.color : 'rgba(255,255,255,0.08)'}`, background: on ? `${m.color}1C` : 'rgba(255,255,255,0.02)', boxShadow: on ? `0 0 16px ${m.color}50` : 'none' }}>
                <CrystalIcon kind={id} size={52} glow={on} />
                <span className="font-['Outfit'] text-[11px] uppercase tracking-[0.08em] font-semibold" style={{ color: on ? m.color : 'rgba(255,255,255,0.6)' }}>{m.label}</span>
                <span className="font-['Outfit'] text-[9px] text-white/40 text-center leading-tight">{m.hint}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">2 · Say it in one line</p>
        <form onSubmit={submit} className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value.slice(0, 140))} data-testid="oath-hit-input" placeholder={k.placeholder}
            className="flex-1 rounded-full px-4 py-3 bg-white/[0.04] font-['Outfit'] text-sm text-white placeholder:text-white/25 outline-none transition-colors"
            style={{ border: `1px solid ${k.color}66`, caretColor: k.color }} />
          <button type="submit" disabled={!text.trim() || busy} data-testid="oath-hit-submit"
            className="w-12 h-12 rounded-full flex items-center justify-center active:scale-90 transition-transform disabled:opacity-35"
            style={{ background: `linear-gradient(135deg, ${k.color}, ${k.color}99)`, boxShadow: `0 0 16px ${k.color}80` }}>
            <Plus size={22} color="#0A0A0A" />
          </button>
        </form>
        <p className="font-['Outfit'] text-[10px] text-white/30 mt-1.5 ml-2">{state.hits_today >= DAILY_HIT_GOAL ? '+10 power per hit · RUSH active' : '+5 power per hit · first 3 unlock RUSH'}</p>
      </div>
      <div className="space-y-2" data-testid="oath-hits-list">
        <AnimatePresence initial={false}>
          {today.map((h) => {
            const m = kindOf(h.kind);
            return (
              <motion.div key={h.id} layout initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: `${m.color}0D`, borderLeft: `2px solid ${m.color}` }}>
                <CrystalIcon kind={h.kind} size={26} />
                <p className="flex-1 font-['Outfit'] text-sm text-white/85">{h.text}</p>
                <button type="button" onClick={() => onDelete(h.id)} aria-label="Remove" data-testid={`oath-hit-delete-${h.id}`} className="text-white/25 hover:text-white/60"><X size={14} /></button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      <p className="font-['Outfit'] text-[10px] text-white/30 text-center tracking-[0.15em] uppercase">Lifetime gratitude · <span style={{ color: P.accent }} data-testid="oath-total-hits">{state.total_hits}</span></p>
    </div>
  );
};

export default OathHits;
